import React, { useEffect, useState } from 'react';
import { Box, TextField, Autocomplete, Fab, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { IAppointment, AppointmentUpdateServiceIdsRequest } from "@ontime/types";
import { useSelector } from 'react-redux';
import { selectOrganizationTasks } from '../../stateManagement/organizationTaskSlice';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import AppointmentService from '../../services/AppointmentService';
import { green } from '@mui/material/colors';
import InfoComponent from '../../customComponents/InfoComponent';
import { selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { selectSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';

const appointmentSchema = z.object({
    appointmentId: z.string(),
    version: z.number(),
    serviceProviderId: z.string(),
    serviceIds: z.string().max(100, "Maximum length of serviceId is 100 characters.").optional(),
    startDateTime: z.string(),
    endDateTime: z.string().optional(),
});

interface AppointmentFormProps {
    closeForm: (input?: IAppointment) => void;
    updateExistData: (input: IAppointment) => void;
    appointment: IAppointment;
    disabled: boolean;
}

const AppointmentServiceIdsForm: React.FC<AppointmentFormProps> = ({ closeForm, appointment, updateExistData, disabled }) => {
    const { enqueueSnackbar } = useSnackbar();

    const organizationTasks = useSelector(selectOrganizationTasks);


    const selectedOrganization = useSelector(selectSelectedOrganization);
    const selectedServiceProvider = useSelector(selectSelectedServiceProvider);



    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<AppointmentUpdateServiceIdsRequest>({
        resolver: zodResolver(appointmentSchema),
        defaultValues: {
            appointmentId: '',
            serviceProviderId: selectedServiceProvider.serviceProviderId,
            serviceIds: '',
            version: 0,
            startDateTime: '',
            endDateTime: '',
        },
    });

    useEffect(() => {
        if (appointment) {
            reset({
                appointmentId: appointment.appointmentId,
                serviceIds: appointment.serviceIds,
                serviceProviderId: selectedServiceProvider.serviceProviderId,
                version: appointment.version,
                startDateTime: appointment.startDateTime,
                endDateTime: appointment.endDateTime,
            });

        }
    }, [appointment, reset]);

    const [serviceIdsLoading, setServiceIdsLoading] = useState(false);
    const handleSave = async (input: AppointmentUpdateServiceIdsRequest) => {
        setServiceIdsLoading(true);

        try {

            const amount = (input.serviceIds
                ?.split(',')
                .map((id) => {
                    const service = organizationTasks.find((os) => os.organizationTaskId === id);
                    return service?.price ?? 0;
                }) ?? []).reduce((sum, value) => sum + value, 0);

            const response = await AppointmentService.UpdateServiceIds(selectedOrganization.organizationId, selectedServiceProvider.serviceProviderId, { ...input, amount: amount });

            updateExistData({ ...appointment, serviceIds: response.serviceIds, amount: response.amount, version: response.version });

        } catch (error) {
            console.error(error, { variant: 'error' });
        } finally {
            setServiceIdsLoading(false);
        }
    };

    const serviceIds = watch('serviceIds');

    const isDirty =
        serviceIds !== appointment.serviceIds

    return (

        <form onSubmit={handleSubmit(handleSave)} noValidate>
            <InfoComponent infoContent='Based on the services you choose, you can specify the amount and duration for appointment.' >
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={organizationTasks.filter(s => !(s.isDeleted ?? false))}
                        getOptionLabel={(option) => option.title}
                        value={organizationTasks.filter(service =>
                            watch('serviceIds')?.split(',').map(String).includes(service.organizationTaskId)
                        )}
                        onChange={(_, selectedServices) => {
                            const ids = selectedServices.map(s => s.organizationTaskId).join(',');
                            setValue('serviceIds', ids);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Services"
                                InputLabelProps={{ shrink: true }}
                                error={!!errors.serviceIds}
                                helperText={errors.serviceIds?.message}

                            />
                        )}
                        fullWidth
                        disabled={serviceIdsLoading || disabled}
                    />
                    <Box sx={{ position: 'relative' }}>
                        <Fab disabled={serviceIdsLoading || !isDirty || disabled} size='small' aria-label="save" color="primary" type="submit">
                            {!isDirty ? <CheckIcon /> : <SaveIcon />}
                        </Fab>
                        {serviceIdsLoading && (
                            <CircularProgress
                                size={48}
                                sx={{
                                    color: green[500],
                                    position: 'absolute',
                                    top: -4,
                                    left: -4,
                                    zIndex: 1,
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </InfoComponent>
        </form>
    );
};

export default AppointmentServiceIdsForm;